import React, { useMemo } from 'react';
import { Card, Alert, Button } from 'react-bootstrap';
import { Form, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { map, filter } from 'lodash';
import { useUpdateEffect } from 'usehooks-ts';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FormGroupControl, FormGroupSelect } from '@components/Base';
import { useInsurersFetch } from '@components/Feature/Insurer';
import { ActivationDateControl } from '@components/Feature/Quote';
import { useHealthPlansFetch } from '@components/Feature/HealthPlan';
import { useCompanyUsersFetch } from '@components/Feature/User';

import { FormDisabled, FormRequired, FormValues } from './type';

const GROUP_PROPS = { className: 'mb-3' };

type Props = {
  disabled?: FormDisabled;
  required: FormRequired;
};

const CorporateHealthQuoteBuyForm = ({ disabled, required }: Props) => {
  const { t } = useTranslation();
  const insurers = useInsurersFetch();
  const healthPlans = useHealthPlansFetch();
  const companyUsers = useCompanyUsersFetch();
  const { values, isSubmitting, setFieldValue } = useFormikContext<FormValues>();
  const { insurerId } = values;
  const insurerHealthPlans = useMemo(
    () => filter(healthPlans, (x) => x.insurerId == insurerId),
    [healthPlans, insurerId],
  );

  useUpdateEffect(() => {
    if (!insurerId) return;
    setFieldValue('healthPlanId', '');
  }, [insurerId]);

  return (
    <Form>
      <Alert variant="warning">{t('personalInfo.changeabilityWarningMessage')}</Alert>

      <Card>
        <Card.Body>
          <Card.Title>{t('common:fillPolicyDetails')}</Card.Title>
          <FormGroupSelect
            name="insurerId"
            label={t('common:insurer')}
            required={required.insurerId}
            disabled={disabled?.insurerId}
            groupProps={GROUP_PROPS}
          >
            <option></option>
            {map(insurers, ({ id, name }) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </FormGroupSelect>

          <FormGroupSelect
            name="healthPlanId"
            label={t('common:healthPlan')}
            required={required.healthPlanId}
            disabled={disabled?.healthPlanId}
            groupProps={GROUP_PROPS}
          >
            <option></option>
            {map(insurerHealthPlans, ({ id, name }) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </FormGroupSelect>

          <FormGroupSelect
            name="userId"
            label={t('common:user')}
            required={required.userId}
            disabled={disabled?.userId}
            groupProps={GROUP_PROPS}
          >
            <option></option>
            {map(companyUsers, ({ id, name, company }) => (
              <option key={id} value={id}>
                {company.name} - {name}
              </option>
            ))}
          </FormGroupSelect>

          <FormGroupControl
            name="thirdPartyAdminEmail"
            label="Third party administrator email"
            required={required.thirdPartyAdminEmail}
            disabled={disabled?.thirdPartyAdminEmail}
            groupProps={GROUP_PROPS}
          />

          <ActivationDateControl required={required.activationDate} disabled={disabled?.activationDate} />

          <FormGroupControl
            name="adminFeeAmount"
            label="Admin Fee"
            required={required.adminFeeAmount}
            disabled={disabled?.adminFeeAmount}
            groupProps={GROUP_PROPS}
            type="number"
          />

          <FormGroupControl
            name="proof"
            type="file"
            label={t('proof')}
            required={required.proof}
            disabled={disabled?.proof}
            groupProps={GROUP_PROPS}
          />

          <FormGroupControl
            name="note"
            as="textarea"
            rows={4}
            label="Note"
            required={required.note}
            disabled={disabled?.note}
            groupProps={GROUP_PROPS}
          />
        </Card.Body>

        <Card.Footer className="bg-white border-0">
          <div className="text-center my-4">
            <Button type="submit" variant="primary" size="lg" disabled={isSubmitting}>
              <span className="me-2">
                {t('common:summary')} & {t('common:review')}
              </span>
              <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </div>
        </Card.Footer>
      </Card>
    </Form>
  );
};

export { CorporateHealthQuoteBuyForm };
